/**
 * Estado de la sesión: quién soy y qué puedo hacer.
 *
 * `GuardiaDeSesion` envuelve las pantallas privadas: mientras carga muestra el esqueleto de la
 * app (`SesionCargando`), si no hay sesión muestra la pantalla de ingreso, y si hay sesión entrega los datos de la persona a todos
 * los componentes de adentro mediante `useYo()`.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, type ReactNode } from 'react';
import type { Yo } from '@cdtec-backlog/contrato';
import { mensajeDeError } from '../../lib/api';
import { cerrarSesion, obtenerYo } from './api';
import { PantallaIngreso } from './PantallaIngreso';
import { SesionCargando } from './SesionCargando';

const ContextoYo = createContext<Yo | null>(null);

/** La persona con sesión. Solo se puede usar dentro de `GuardiaDeSesion`. */
export function useYo(): Yo {
  const yo = useContext(ContextoYo);
  if (!yo) throw new Error('useYo debe usarse dentro de <GuardiaDeSesion>.');
  return yo;
}

/** Cierra la sesión y vuelve a la pantalla de ingreso. */
export function useCerrarSesion() {
  const cliente = useQueryClient();
  return async () => {
    await cerrarSesion();
    // Se descartan los datos de la sesión anterior: nada de eso debe quedar visible ni en memoria.
    cliente.removeQueries({ predicate: (consulta) => consulta.queryKey[0] !== 'yo' });
    // Se marca «sin sesión» directamente. Volver a preguntar al servidor no hace falta y, si se
    // limpiara toda la caché, la pantalla no se enteraría de que debe volver al ingreso.
    cliente.setQueryData(['yo'], null);
  };
}

export function GuardiaDeSesion({ children }: { children: ReactNode }) {
  const consulta = useQuery({
    queryKey: ['yo'],
    queryFn: obtenerYo,
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (consulta.isPending) return <SesionCargando />;
  if (consulta.isError)
    return (
      <p className="sesion-estado" role="alert">
        {mensajeDeError(consulta.error)}
      </p>
    );
  if (!consulta.data) return <PantallaIngreso />;

  return <ContextoYo.Provider value={consulta.data}>{children}</ContextoYo.Provider>;
}
